import React, { useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames';
import { Icon, Hidden, Button, Typography, H2 } from '@vp/swan';
import { TrackingContext } from '../../../hooks/useTracking';
import { VatContext } from '../../../contexts/VatContext';
import { useCustomerExperienceContextModule } from './useCustomerExperienceModule';
import { VatPopProps } from '../../../types';

declare global {
  interface Document {
    pricingContextModule: any;
  }
}

const trackingConstants = {
  trackingLabel: 'VAT Pop-Up',
  inclusiveDetail: 'See prices inc. 20% VAT',
  exclusiveDetail: 'See prices ex. 20% VAT',
  popUpViewed: 'Pop-Up Viewed',
  popUpClicked: 'Pop-Up Clicked',
  popUpClosed: 'Pop-Up Closed',
};

const excludedInteractionIds = [
  'consent-manager-container',
  'vatSelectionPop',
  'sitewide-popup-capture-click',
  'example-page-reload-test-zone',
];

const popInteractionButtonIds = {
  close: 'vatSelectionCloseButton',
  inclusion: 'vatSelectionInclusionButton',
  exclusion: 'vatSelectionExclusionButton',
};

export const VatPop: React.FC<VatPopProps> = () => {
  const {
    vatPop,
    vatPopTitle,
    vatPopSubheading,
    vatPopNote,
    vatPopCloseLabel,
    vatPopExclusionText,
    vatPopExclusionButtonLabel,
    vatPopInclusionText,
    vatPopInclusionButtonLabel,
    vatLocale,
    pricingContextScriptLoaded,
    setVatInclusivity,
    displayVatPop,
    setDisplayVatPop,
  } = useContext(VatContext);
  const { setVatPopShown, vatPopShown, setPersistVatPop } =
    useCustomerExperienceContextModule();
  const { trackPopInteraction } = useContext(TrackingContext);

  const closePop = useCallback(() => {
    setPersistVatPop(false);
    setDisplayVatPop?.(false);
  }, [setDisplayVatPop, setPersistVatPop]);

  useEffect(() => {
    if (
      pricingContextScriptLoaded &&
      vatPop &&
      vatLocale &&
      !vatPopShown &&
      !displayVatPop
    ) {
      setDisplayVatPop?.(true);
      setVatPopShown(true);
      trackPopInteraction(
        trackingConstants.popUpViewed,
        trackingConstants.trackingLabel
      );
    }
  }, [
    vatPopShown,
    vatLocale,
    setVatPopShown,
    setDisplayVatPop,
    vatPop,
    pricingContextScriptLoaded,
    trackPopInteraction,
    displayVatPop,
  ]);

  useEffect(() => {
    const clickHandler = function (evt: any) {
      const path = evt.composedPath();
      if (
        path.find((el: any) =>
          Object.values(popInteractionButtonIds).includes(
            (el as HTMLElement).id
          )
        )
      ) {
        return;
      }
      if (
        !path.find((el: any) =>
          excludedInteractionIds.includes((el as HTMLElement).id)
        )
      ) {
        closePop();
      } else {
        // in case an interaction with the other section opens a new page or refreshes,
        // the vat pop should still be there
        if (displayVatPop) {
          setPersistVatPop(true);
        }
      }
    };
    document.addEventListener('click', clickHandler);
    document.addEventListener('scroll', closePop);
    document.addEventListener('navHover', closePop);
    return () => {
      document.removeEventListener('click', clickHandler);
      document.removeEventListener('scroll', closePop);
      document.removeEventListener('navHover', closePop);
    };
  }, [closePop, displayVatPop, setPersistVatPop]);

  const vatInclusivityClicked = (vatInclusivity: boolean) => {
    if (setVatInclusivity) {
      setVatInclusivity(vatInclusivity);
      const eventDetail = vatInclusivity
        ? trackingConstants.inclusiveDetail
        : trackingConstants.exclusiveDetail;
      trackPopInteraction(
        trackingConstants.popUpClicked,
        trackingConstants.trackingLabel,
        eventDetail
      );
      closePop();
    }
  };

  return (
    (<div
              id="vatSelectionPop"
              role="dialog"
              aria-labelledby="vatPopTitle"
              className={classNames({ 'vat-pop-open': displayVatPop })}
            >
      <button
        onClick={() => {
          trackPopInteraction(
            trackingConstants.popUpClosed,
            trackingConstants.trackingLabel
          );
          closePop();
        }}
        data-section={trackingConstants.trackingLabel}
        data-position={1}
        data-translation={trackingConstants.popUpClosed}
        className="vat-pop-close-button"
        id={popInteractionButtonIds.close}
        tabIndex={displayVatPop ? 0 : -1}
      >
        <Icon iconType="close" />
        <span className="swan-visually-hidden">{vatPopCloseLabel}</span>
      </button>
      <H2
        marginBottom={0}
        fontSize={"standard"}
        fontWeight="bold"
        textAlign="center"
        className="vat-pop-title"
        id="vatPopTitle"
      >
        {vatPopTitle}
      </H2>
      {vatPopSubheading && (
        <Typography fontSize={"small"} textAlign="center" marginTop={"7"}>
          {vatPopSubheading}
        </Typography>
      )}
      {vatPopExclusionText && (
        <Typography
          fontSize={"small"}
          fontWeight="bold"
          textAlign="center"
          marginTop={"7"}
          id="vatPopExclusiveLabel"
        >
          {vatPopExclusionText}
        </Typography>
      )}
      <Button
        skin="primary"
        size="mini"
        marginTop={vatPopInclusionText ? "6" : "7"}
        onClick={() => vatInclusivityClicked(false)}
        aria-describedby="vatPopExclusiveLabel"
        data-section={trackingConstants.trackingLabel}
        data-position={2}
        data-translation={`${trackingConstants.popUpClicked}:${trackingConstants.exclusiveDetail}`}
        id={popInteractionButtonIds.exclusion}
        tabIndex={displayVatPop ? 0 : -1}
      >
        {vatPopExclusionButtonLabel}
      </Button>
      {vatPopInclusionText && (
        <Typography
          fontSize={"small"}
          fontWeight="bold"
          textAlign="center"
          marginTop={"7"}
          id="vatPopInclusiveLabel"
        >
          {vatPopInclusionText}
        </Typography>
      )}
      <Button
        skin="secondary"
        size="mini"
        marginTop={"6"}
        onClick={() => vatInclusivityClicked(true)}
        onKeyDown={(evt: React.KeyboardEvent<HTMLButtonElement>) => {
          if (evt.key === 'Tab' && !evt.shiftKey) {
            //hide pop if exiting wihtout making a selection during keyboard nav
            setDisplayVatPop?.(false);
          }
        }}
        aria-describedby="vatPopInclusiveLabel"
        data-section={trackingConstants.trackingLabel}
        data-position={3}
        data-translation={`${trackingConstants.popUpClicked}:${trackingConstants.inclusiveDetail}`}
        id={popInteractionButtonIds.inclusion}
        tabIndex={displayVatPop ? 0 : -1}
      >
        {vatPopInclusionButtonLabel}
      </Button>
      {vatPopNote && (
        <Hidden xs sm>
          <Typography
            fontSize={"small"}
            textAlign="center"
            textColor="subtle"
            marginTop={"7"}
          >
            {vatPopNote}
          </Typography>
        </Hidden>
      )}
    </div>)
  );
};
